import "../analysis/styles/Analysis.css"
import { useNavigate } from "react-router-dom"
import React, { useEffect, useState } from "react"
import { RootState } from "../../../store"
import { connect } from "react-redux"
import { CoManagerReleasePlan, User } from "../../../types"
import { KButton, KSpan } from "kahuna-base-react-components"
import { PERMISSIONS, USER_ROLE } from "../../../constants"
import { getSelectedLanguage, lang } from "../../../constants/languages"
import { HoveredIndex, PlatformsLineChartComponent } from "../trends/streams-daily/StreamsDaily"
import { overview, releasePlans, summaries, trends } from "../../../apis"
import { getCurrency, hashids, mapToCamelCase } from "../../../utility"
import ReleasePlanPanel from "../co-manager/ReleasePlanPanel"

type OverviewData = {
  nextRelease: { id: number; title: string; releaseDate: string } | null
  streams: { last7Days: number; previous7Days: number; daily: { date: string; streams: number }[] }
}

// The figures of the royalties page's balance widget this page needs, read the way that widget reads them.
type BalanceTotals = {
  payout?: number
  closingBalance?: number
  paymentThreshold?: number
  paymentDate?: string
  currency?: string
}

const DAY_MS = 24 * 60 * 60 * 1000

const fill = (template: string, values: { [key: string]: string }) =>
  Object.entries(values).reduce((text, [key, value]) => text.split(`{${key}}`).join(value), template)

const money = (amount: number) => amount.toLocaleString("en-EN", { maximumFractionDigits: 2 })

const formatDate = (date: Date, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(getSelectedLanguage(), options).format(date)

// Payouts land on the last day of their payment month, YYYYMM, the same reading the balance widget makes.
const paymentDay = (paymentDate: string) =>
  new Date(Number(paymentDate.substring(0, 4)), Number(paymentDate.substring(4, 6)), 0)

const Overview = ({ newReleasePermission, user }: { newReleasePermission: boolean; user: User }) => {
  const [data, setData] = useState<OverviewData | null>(null)
  const [balance, setBalance] = useState<BalanceTotals | null>(null)
  const [plans, setPlans] = useState<CoManagerReleasePlan[]>([])
  const [plansLoaded, setPlansLoaded] = useState(false)
  const [streamsDaily, setStreamsDaily] = useState([])
  const [hoveredIndex, setHoveredIndex] = useState<HoveredIndex>({
    datasetIndex: -1,
    dataIndex: -1
  })

  const navigate = useNavigate()
  const words = lang.overview

  const getStreamsDailyTopTracks = () => {
    // Streams only, as the Streams card counts them: video views would bury them under YouTube.
    trends(`/streams_last_7_days/?event_type=dsp`, "get")
      .then((resp) => {
        if (resp.data.results) {
          const streamsDaily = resp.data.results
          const top5Isrcs = [...streamsDaily].reduce((acc, item) => {
            acc[item.isrc] = (acc[item.isrc] || 0) + item.streams
            return acc
          }, {} as Record<string, number>)

          const top5 = Object.entries(top5Isrcs)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([isrc]) => isrc)

          // use this only when rendering:
          const filtered = streamsDaily.filter((d) => top5.includes(d.isrc))
          setStreamsDaily(filtered)
        }
      })
      .catch(() => {})
  }

  useEffect(() => {
    getStreamsDailyTopTracks()
    // Each card fills in on its own: one slow source never holds the others back.
    overview("/", "get")
      .then((response) => setData(mapToCamelCase<OverviewData>(response.data)))
      .catch(() => setData(null))
    // The main account and its advance accounts, the artist's own money: label members are not theirs.
    summaries(
      "/royalties_balance/?limit=1&offset=0&is_admin_page=0&include_totals=1&summary_type=platform&selected_account=advance_and_main",
      "get"
    )
      .then((response) => setBalance(response.data.totals ? mapToCamelCase<BalanceTotals>(response.data.totals) : null))
      .catch(() => setBalance(null))
    /*releasePlans("/", "get")
      .then((response) => setPlans(mapToCamelCase<{ results: CoManagerReleasePlan[] }>(response.data).results))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoaded(true))*/
  }, [])

  const renderCard = (props: {
    icon: string
    title: string
    value: string
    sub?: string
    subColor?: string
    onClick: () => void
    dataTestId: string
    extra?: React.ReactNode
    // Words rather than a figure, set smaller so a sentence does not shout like an amount.
    textValue?: boolean
  }) => (
    <div className="overview-stat-card" onClick={props.onClick} data-testid={props.dataTestId}>
      <div className="flex items-center gap-2">
        <img src={props.icon} className="overview-stat-icon" />
        <KSpan text={props.title} fontSize={13} lineHeight="18px" color="#666" />
      </div>
      <div className="overview-stat-body">
        <div className="flex flex-col gap-1 min-w-0">
          <KSpan
            text={props.value}
            fontSize={props.textValue ? 18 : 24}
            lineHeight="32px"
            fontWeight={500}
            color="#111"
            ellipsis
          />
          {props.sub && <KSpan text={props.sub} fontSize={12} lineHeight="16px" color={props.subColor || "#999"} />}
        </div>
        {props.extra}
      </div>
    </div>
  )

  const renderBalanceCard = () => {
    const payout = balance?.payout || 0
    const total = payout + (balance?.closingBalance || 0)
    return renderCard({
      icon: "/admin_icons/wallet.svg",
      title: words.balance_title,
      value: balance ? `${getCurrency(balance.currency || "")}${money(total)}` : "-",
      sub: !balance
        ? undefined
        : payout > 0
        ? fill(words.balance_payable, { amount: `${getCurrency(balance.currency || "")}${money(payout)}` })
        : balance.paymentThreshold
        ? fill(words.balance_threshold, {
            amount: `${getCurrency(balance.currency || "")}${money(balance.paymentThreshold)}`
          })
        : undefined,
      onClick: () => navigate("/royalties"),
      dataTestId: "overview-balance-card"
    })
  }

  const renderStreamsCard = () => {
    const streams = data?.streams
    const daily = streams?.daily || []
    const peak = Math.max(1, ...daily.map((day) => day.streams))
    const change =
      streams && streams.previous7Days > 0
        ? Math.round(((streams.last7Days - streams.previous7Days) / streams.previous7Days) * 100)
        : null
    return renderCard({
      icon: "/navigation_icons/soundwave.svg",
      title: words.streams_title,
      value: streams ? streams.last7Days.toLocaleString("en-EN") : "-",
      sub: !streams?.last7Days
        ? words.streams_no_data
        : change !== null
        ? fill(words.streams_change, { change: `${change > 0 ? "+" : ""}${change}%` })
        : undefined,
      subColor: change === null || !streams?.last7Days ? undefined : change >= 0 ? "#3E9E1F" : "#FF5C5C",
      onClick: () => navigate("/insights"),
      dataTestId: "overview-streams-card",
      extra: daily.length > 1 && (
        <div className="overview-stat-bars">
          {daily.map((day) => (
            <div
              key={`overview-streams-${day.date}`}
              className="overview-stat-bar"
              style={{ height: `${Math.max(8, (day.streams / peak) * 100)}%` }}
            />
          ))}
        </div>
      )
    })
  }

  /**
   * The release closest to coming out. A delivered release with a date wins its day, but a planned one
   * counts too: a plan's draft is not scheduled at the stores yet, and it is still what the artist has
   * coming next.
   */
  const renderUpcomingCard = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
      today.getDate()
    ).padStart(2, "0")}`
    const release = data?.nextRelease
    const plan = plans
      .filter((item) => item.releaseDate >= todayIso && item.releaseId !== release?.id)
      .sort((first, second) => first.releaseDate.localeCompare(second.releaseDate))[0]
    const next =
      release && (!plan || release.releaseDate <= plan.releaseDate)
        ? {
            title: release.title,
            releaseDate: release.releaseDate,
            path: `/catalog/${hashids.encode(String(release.id))}`
          }
        : plan
        ? { title: plan.title, releaseDate: plan.releaseDate, path: "/catalog/release-plan", planned: true }
        : null

    if (!next) {
      return renderCard({
        icon: "/account_icons/music-album.svg",
        title: words.upcoming_title,
        value: words.upcoming_none,
        textValue: true,
        sub: newReleasePermission ? words.upcoming_none_hint : undefined,
        onClick: () => navigate(newReleasePermission ? "/catalog/new-release" : "/catalog"),
        dataTestId: "overview-upcoming-card"
      })
    }

    const releaseDate = new Date(`${next.releaseDate}T00:00:00`)
    const days = Math.round((releaseDate.getTime() - today.getTime()) / DAY_MS)
    const when = formatDate(releaseDate, { day: "numeric", month: "long" })
    return renderCard({
      icon: "/account_icons/music-album.svg",
      title: words.upcoming_title,
      value: next.title,
      textValue: true,
      sub: [
        days > 0
          ? fill(words.upcoming_in_days, { date: when, days: String(days) })
          : fill(words.upcoming_today, { date: when }),
        "planned" in next ? words.upcoming_planned : ""
      ]
        .filter(Boolean)
        .join(" · "),
      onClick: () => navigate(next.path),
      dataTestId: "overview-upcoming-card"
    })
  }

  const renderPaymentCard = () => {
    const payout = balance?.payout || 0
    const aboveThreshold = !!balance && payout > 0 && payout >= (balance.paymentThreshold || 0)
    const widget = lang.royalties.balance.widget
    return renderCard({
      icon: "/calendar_date.svg",
      title: words.payment_title,
      textValue: true,
      value:
        aboveThreshold && balance?.paymentDate
          ? formatDate(paymentDay(balance.paymentDate), { weekday: "short", day: "numeric", month: "long" })
          : "-",
      sub:
        !balance || aboveThreshold
          ? undefined
          : (balance.closingBalance || 0) < 0
          ? widget.recoupment_in_progress
          : widget.under_payment_threshold,
      onClick: () => navigate("/royalties"),
      dataTestId: "overview-payment-card"
    })
  }

  const renderReleasePlans = () => (
    <div className="overview-section" data-testid="overview-release-plans">
      <div className="overview-section-head">
        <KSpan text={words.release_plans_title} fontSize={18} lineHeight="24px" fontWeight={500} color="#111" />
        {plans.length > 0 && (
          <KButton
            dataTestId="overview-release-plans-see-all"
            text={words.release_plans_see_all}
            onClick={() => navigate("/catalog/release-plan")}
            background="transparent"
            shadowDisabled
            width="auto"
            height="auto"
            padding="0px"
            fontSize={12}
            textColor="#6B7280"
          />
        )}
      </div>
      {!plansLoaded ? (
        <KSpan text={lang.co_manager.release_plan_page_loading} fontSize={13} color="#999" />
      ) : plans.length > 0 ? (
        <div className="overview-release-plans">
          <ReleasePlanPanel plans={plans} onChange={setPlans} />
        </div>
      ) : (
        <div className="overview-release-plans-empty">
          <KSpan text={words.release_plans_empty} fontSize={13} lineHeight="18px" color="#666" />
          <KButton
            dataTestId="overview-release-plans-create"
            text={words.release_plans_create}
            onClick={() => navigate("/catalog/release-plan")}
            background="#111"
            hoverBackground="#333"
            textColor="#FFF"
            width="auto"
            height="36px"
            padding="8px 16px"
            borderRadius={100}
            fontSize={14}
            shadowDisabled
          />
        </div>
      )}
    </div>
  )

  const renderFirstQuickLink = () => {
    return newReleasePermission ? (
      <div className="quick-access-div" onClick={() => navigate(`/catalog/new-release`)} data-testid="new-release-card">
        <img src="/navigation_icons/music.svg" className="quick-access-img" />
        <div className="mt-5">
          <KSpan text={lang.overview.new_release_header} color="#000" fontSize={16} fontWeight={500} />
        </div>
        <div className="mt-3">
          <KSpan text={lang.overview.new_release_desc} />
        </div>
      </div>
    ) : (
      <div className="quick-access-div" onClick={() => navigate("/account/profile")}>
        <img src="/navigation_icons/profile_dropdownmenu_icons/user-filled.svg" className="quick-access-img" />
        <div className="mt-5">
          <KSpan text={lang.overview.profile_header || "Profile"} color="#000" fontSize={16} fontWeight={500} />
        </div>
        <div className="mt-3">
          <KSpan
            text={lang.overview.profile_desc || "Let's go to the profile page and make sure everything is up to date."}
          />
        </div>
      </div>
    )
  }

  const renderThirdQuickLink = () => {
    return user.role?.id === USER_ROLE.Affiliate ? (
      <div className="quick-access-div" onClick={() => navigate("/affiliate")}>
        <div className="flex justify-between items-center">
          <img src="/navigation_icons/affiliate.svg" className="quick-access-img" />
        </div>
        <div className="mt-5">
          <KSpan text={lang.overview.affiliate_header} color="#000" fontSize={16} fontWeight={500} />
        </div>
        <div className="mt-3">
          <KSpan text={lang.overview.affiliate_desc} />
        </div>
      </div>
    ) : (
      <div className="quick-access-div" onClick={() => navigate("/artist-space")}>
        <div className="flex justify-between items-center">
          <img src="/navigation_icons/earth.svg" className="quick-access-img" />
        </div>
        <div className="mt-5">
          <KSpan text={lang.overview.artist_space_header} color="#000" fontSize={16} fontWeight={500} />
        </div>
        <div className="mt-3">
          <KSpan text={lang.overview.artist_space_desc} />
        </div>
      </div>
    )
  }

  return (
    <div className="overview-page pb-8">
      <div className="overview-stats">
        {renderBalanceCard()}
        {renderStreamsCard()}
        {renderUpcomingCard()}
        {renderPaymentCard()}
      </div>
      {
        //renderReleasePlans()
      }
      {streamsDaily.length > 0 && (
        <div className="overview-section">
          <KSpan text={words.top_5_tracks} fontSize={18} lineHeight="24px" fontWeight={500} color="#111" />
          <div className="flex-grow min-h-0 w-full" style={{ height: 300 }}>
            <PlatformsLineChartComponent data={streamsDaily} hoveredIndex={hoveredIndex} setHoveredIndex={() => {}} />
          </div>
        </div>
      )}
      <div className="overview-section">
        <KSpan text={lang.overview.title} fontSize={18} lineHeight="24px" fontWeight={500} color="#111" />
        <div
          className="flex p-2"
          style={{
            width: "100%",
            borderRadius: 10,
            border: "1px solid var(--stroke-soft-200, #F3F3F3)",
            background: "var(--bg-white-0, #FFF)",
            boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
          }}
        >
          {renderFirstQuickLink()}
          <div className="mt-2 mb-2" style={{ borderRight: "1px solid #f7f7f7" }} />
          <div className="quick-access-div" onClick={() => navigate("/royalties")}>
            <div className="flex justify-between items-center">
              <img src="/navigation_icons/presentation-chart.svg" className="quick-access-img" />
            </div>
            <div className="mt-5">
              <KSpan text={lang.overview.royalties_header} color="#000" fontSize={16} fontWeight={500} />
            </div>
            <div className="mt-3">
              <KSpan text={lang.overview.royalties_desc} />
            </div>
          </div>
          <div className="mt-2 mb-2" style={{ borderRight: "1px solid #f7f7f7" }} />
          {renderThirdQuickLink()}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user,
    newReleasePermission: state.auth.user.permissionUser?.some((item) => {
      return item.permission && item.permission.id === PERMISSIONS.NewRelease
    })
  }
}

export default connect(mapStateToProps)(Overview)
